import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { AppStore } from "../app/store";
import { ActionType, MctToastType } from "../@types";
import { Dialog, Button } from "@cmsgov/ds-medicare-gov";
import { hasLoginSession } from "../helpers/loginHelpers";
import { differenceInMilliseconds } from "date-fns";
import { sendLogoutRequest, sendKeepAlive } from "../api";
import { withRouter, RouteComponentProps } from "react-router";
import { logError, useTranslate } from "../helpers";
import {
  USER_INACTIVITY_TIMEOUT_IN_MS,
  USER_LOGOUT_TIMEOUT_IN_MS,
} from "../config";
import { FormattedMessage } from "react-intl-phraseapp";
import { ApiResponseError } from "../helpers/errors";

const UserActivityMonitor: FunctionComponent<RouteComponentProps> = ({
  children,
  history,
}) => {
  const {
    state: { beneficiary, lastActivity },
    dispatch,
  } = useContext(AppStore.AppContext);

  const loggedIn = hasLoginSession();

  const [showInactiveDialog, setShowInactiveDialog] = useState(false);
  const [inactivityTimeout, setInactivityTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);
  const [logoutTimeout, setLogoutTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);
  const [sendingKeepAlive, setSendingKeepAlive] = useState(false);

  const t = useTranslate();

  const lastActivityRef = useRef(lastActivity);
  const loggedInRef = useRef(loggedIn);

  const activityEvents = ["click", "mousemove", "keydown", "wheel", "scroll"];

  const inactiveTimeoutInMs = USER_INACTIVITY_TIMEOUT_IN_MS;
  const logoutTimeoutInMs = USER_LOGOUT_TIMEOUT_IN_MS;

  const logout = async (
    apiExpired?: boolean,
    clientExpired?: boolean
  ): Promise<void> => {
    if (!apiExpired) {
      try {
        await sendLogoutRequest();
      } catch (e) {
        logError("Could not log out user (UserActivityMonitor)", e);
      }
    }

    setShowInactiveDialog(false);

    history.push(
      `/?mctt=${
        clientExpired ? MctToastType.SESSION_EXPIRED : MctToastType.LOGGED_OUT
      }`
    );

    dispatch({ type: ActionType.RESET_STATE });
  };

  const keepAlive = async (): Promise<void> => {
    if (loggedInRef.current) {
      setSendingKeepAlive(true);

      try {
        await sendKeepAlive();
      } catch (e) {
        if (e instanceof ApiResponseError) {
          const { info } = e;
          const { apiResponseStatus } = info;

          if (apiResponseStatus == 401) {
            logout(true, true);
          }
        }
      }

      setSendingKeepAlive(false);
    }
  };

  // Records activity based on activity events
  const handleActivity = (): void => {
    const curr = new Date();

    if (
      !lastActivityRef.current ||
      (lastActivityRef.current &&
        differenceInMilliseconds(curr, new Date(lastActivityRef.current))) >=
        60 * 1000
    ) {
      if (!sendingKeepAlive) {
        dispatch({ type: ActionType.UPDATE_LAST_ACTIVITY, payload: curr });

        keepAlive();
      }
    }
  };

  const handleNoActivity = (): void => {
    setShowInactiveDialog(true);

    setLogoutTimeout(setTimeout(() => logout(false, true), logoutTimeoutInMs));
  };

  // Every time there is activity, we want to reset the activity timer
  useEffect(() => {
    lastActivityRef.current = lastActivity;

    if (loggedIn && !showInactiveDialog) {
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout);
      }

      setInactivityTimeout(setTimeout(handleNoActivity, inactiveTimeoutInMs));
    }
  }, [lastActivity]);

  const clearTimeouts = (): void => {
    if (inactivityTimeout) {
      clearTimeout(inactivityTimeout);
      setInactivityTimeout(undefined);
    }

    if (logoutTimeout) {
      clearTimeout(logoutTimeout);
      setLogoutTimeout(undefined);
    }
  };

  /**
   * This method handles the case where a user still has an active session,
   * but they've been away from MCT for longer than the inactive timeout.
   * This is called when they access MCT again and we will log them out.
   */
  const handleStaleSession = async (): Promise<void> => {
    if (!loggedIn && beneficiary) {
      dispatch({ type: ActionType.RESET_STATE });
    }
    if (lastActivity && loggedIn && beneficiary) {
      const sinceLastActivity = differenceInMilliseconds(
        new Date(),
        new Date(lastActivity)
      );

      if (sinceLastActivity >= inactiveTimeoutInMs) {
        logout(false, true);
      }
    }
  };

  useEffect(() => {
    handleStaleSession();

    // We always want to record the last activity even if the user isn't logged in
    activityEvents.forEach(eventType =>
      window.addEventListener(eventType, handleActivity)
    );

    return (): void => {
      activityEvents.forEach(eventType =>
        window.removeEventListener(eventType, handleActivity)
      );
    };
  }, []);

  useEffect(() => {
    const hasSessionWithBeneficiary = loggedIn && !!beneficiary;
    loggedInRef.current = hasSessionWithBeneficiary;

    // When the user logs in, start recording activity
    if (hasSessionWithBeneficiary) {
      setInactivityTimeout(setTimeout(handleNoActivity, inactiveTimeoutInMs));
      keepAlive();
    } else {
      clearTimeouts();
    }
  }, [loggedIn, beneficiary]);

  const continueSession = (): void => {
    setShowInactiveDialog(false);

    keepAlive();

    clearTimeouts();
  };

  return (
    <>
      {showInactiveDialog && (
        <Dialog
          getApplicationNode={(): HTMLElement | null =>
            document.getElementById("App")
          }
          escapeExitDisabled={true}
          className="UserActivityMonitor__modal"
          closeText=""
          heading={t("user_activity.title")}
          actions={[
            <Button variation="primary" onClick={continueSession} key="primary">
              {t("user_activity.continue_session")}
            </Button>,
            <Button
              variation="transparent"
              className="ds-c-button--primary-alt"
              key="cancel"
              onClick={(): Promise<void> => logout()}
            >
              {t("user_activity.logout")}
            </Button>,
          ]}
        >
          <FormattedMessage
            id="user_activity.inactive_message"
            values={{
              inactivityMinutes: Math.floor(inactiveTimeoutInMs / 1000 / 60),
              logoutMinutes: Math.floor(logoutTimeoutInMs / 1000 / 60),
            }}
          />
        </Dialog>
      )}
      {children}
    </>
  );
};

export default withRouter(UserActivityMonitor);
